import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { environment } from '../../environments/environment';
import * as XLSX from '@e965/xlsx';

@Component({
    selector: 'app-historic-epod',
    templateUrl: './historic-epod.component.html',
    styleUrls: ['./historic-epod.component.scss'],
    standalone: false
})
export class HistoricEpodComponent implements OnInit {
  orgId: string = '';
  warehouseCode: string = '';
  email: string = '';
  recipientId: string = '';
  showOTPModal: boolean = false;
  otp: string = '';
  otpDisplay: string = '';
  otpInput: string = '';
  loading: boolean = false;
  verifyingOTP: boolean = false;
  error: string = '';
  otpError: string = '';
  isVerified: boolean = false;
  authToken: string = '';

  // Historic Report Modal properties
  showHistoricReportModal: boolean = false;
  historicReportForm: any;
  dateRangeError: string = '';
  isDownloadingReport: boolean = false;
  downloadProgress: string = '';
  currentDate: Date = new Date();
  
  // Download success properties
  showDownloadSuccess: boolean = false;
  downloadSuccessMessage: string = '';
  downloadedRecordsCount: number = 0;
  downloadedDateRange: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private fb: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    // Initialize historic report form
    this.historicReportForm = this.fb.group({
      startDate: [null, Validators.required],
      endDate: [null, Validators.required]
    });

    this.route.queryParams.subscribe(queryParams => {
      this.orgId = queryParams['orgId'] || '';
      this.warehouseCode = queryParams['warehouse_code'] || '';
      this.email = queryParams['email'] || '';
      this.recipientId = queryParams['recipientId'] || '';
      
      if (this.email) {
        this.callOTPAPI();
      }
    });
  }

  callOTPAPI(): void {
    this.loading = true;
    this.error = '';
    
    const url = `${environment.rbacUrl}/rbac/api/v1/user/${this.email}/otp/login`;
    
    this.http.get(url).subscribe({
      next: (response: any) => {
        this.loading = false;
        // Show modal if API call is successful (OTP is sent to email)
        // Extract OTP from response if available (for display purposes)
        let otpValue = response?.otp || response?.data?.otp || response?.otpCode || response?.otp?.code || '';
        
        if (otpValue) {
          // Convert to string, remove any non-numeric characters, ensure exactly 6 digits
          let otpString = String(otpValue).replace(/\D/g, '');
          if (otpString.length < 6) {
            otpString = otpString.padStart(6, '0');
          } else if (otpString.length > 6) {
            otpString = otpString.substring(0, 6);
          }
          this.otp = otpString;
          // Format OTP with spacing for display (e.g., "123 456")
          this.otpDisplay = this.otp.match(/.{1,3}/g)?.join(' ') || this.otp;
        }
        
        // Show modal regardless of whether OTP is in response (OTP is sent to email)
        this.showOTPModal = true;
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Failed to generate OTP';
        console.error('OTP API error:', error);
      }
    });
  }

  closeOTPModal(): void {
    // Only allow closing if verified successfully
    if (this.isVerified) {
      this.showOTPModal = false;
      this.otpInput = '';
      this.otpError = '';
      
      // After closing OTP modal, show the historic report modal
      this.openHistoricReportModal();
    }
  }

  resendOTP(): void {
    // Reset OTP input and error
    this.otpInput = '';
    this.otpError = '';
    this.isVerified = false;
    
    // Call OTP API again
    this.callOTPAPI();
  }

  onOTPInput(event: any): void {
    // Only allow numeric input and limit to 6 digits
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 6) {
      value = value.substring(0, 6);
    }
    this.otpInput = value;
    // Update the input value to reflect the filtered value
    event.target.value = value;
    this.otpError = '';
    
    // Auto-verify when 6 digits are entered
    if (value.length === 6 && !this.verifyingOTP && !this.isVerified) {
      // Use setTimeout to ensure the input value is set before verification
      setTimeout(() => {
        this.verifyOTP();
      }, 100);
    }
  }

  verifyOTP(): void {
    if (!this.otpInput || this.otpInput.length !== 6) {
      this.otpError = 'Please enter a valid 6-digit OTP';
      return;
    }

    this.verifyingOTP = true;
    this.otpError = '';

    const url = `${environment.rbacUrl}/rbac/api/v1/login`;
    const payload = {
      username: this.email,
      password: this.otpInput
    };

    this.http.post(url, payload).subscribe({
      next: (response: any) => {
        this.verifyingOTP = false;
        this.isVerified = true;
        
        // Extract token from response - check multiple possible response structures
        const token = response?.token || 
                     response?.data?.token || 
                     response?.accessToken || 
                     response?.data?.accessToken ||
                     response?.access_token ||
                     response?.data?.access_token ||
                     '';
        
        if (token) {
          this.authToken = token;
          // Store token in localStorage for persistence
          localStorage.setItem('authToken', token);
          localStorage.setItem('userEmail', this.email);
          console.log('Token stored successfully:', token);
        } else {
          console.warn('Token not found in API response:', response);
        }
        
        console.log('OTP verified successfully:', response);
        
        // Auto-close modal after 1.5 seconds if verified successfully
        setTimeout(() => {
          if (this.isVerified) {
            this.closeOTPModal();
          }
        }, 1500);
      },
      error: (error) => {
        this.verifyingOTP = false;
        this.otpError = 'Invalid OTP. Please try again.';
        console.error('OTP verification error:', error);
      }
    });
  }

  // Historic Report Modal methods
  openHistoricReportModal(): void {
    this.showHistoricReportModal = true;
    this.historicReportForm.reset();
    this.dateRangeError = '';
  }

  closeHistoricReportModal(): void {
    if (this.isDownloadingReport) {
      // Don't allow closing while downloading
      return;
    }
    this.showHistoricReportModal = false;
    this.historicReportForm.reset();
    this.dateRangeError = '';
    this.isDownloadingReport = false;
    this.downloadProgress = '';
  }

  // Helper method to ensure date is a Date object
  private ensureDate(date: any): Date | null {
    if (!date) return null;
    if (date instanceof Date) return date;
    // Handle Moment objects (from Angular Material datepicker)
    if (date && typeof date === 'object' && date._isAMomentObject && typeof date.toDate === 'function') {
      return date.toDate();
    }
    // Handle Moment objects by checking for _d property (the underlying Date)
    if (date && typeof date === 'object' && date._d instanceof Date) {
      return date._d;
    }
    if (typeof date === 'string' || typeof date === 'number') {
      return new Date(date);
    }
    return null;
  }

  validateDateRange(): boolean {
    const startDateValue = this.historicReportForm?.get('startDate')?.value;
    const endDateValue = this.historicReportForm?.get('endDate')?.value;
    // Check if dates exist
    if (!startDateValue || !endDateValue) {
      this.dateRangeError = 'Please select both start and end dates';
      return false;
    }
    
    const start = this.ensureDate(startDateValue);
    const end = this.ensureDate(endDateValue);
    
    // Double check after conversion
    if (!start || !end) {
      this.dateRangeError = 'Please select both start and end dates';
      return false;
    }

    if (start > end) {
      this.dateRangeError = 'Start date cannot be greater than end date';
      return false;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 92) {
      this.dateRangeError = `Date range cannot exceed 92 days. Selected range: ${diffDays} days`;
      return false;
    }

    // If we get here, validation passed - clear error
    this.dateRangeError = '';
    return true;
  }

  getMaxEndDate(): Date | null {
    const startDateValue = this.historicReportForm.get('startDate')?.value;
    const startDate = this.ensureDate(startDateValue);
    if (!startDate) {
      return this.currentDate;
    }
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + 92);
    // Return the earlier of (start date + 92 days) or current date
    return maxDate > this.currentDate ? this.currentDate : maxDate;
  }

  onStartDateChange(event: MatDatepickerInputEvent<Date>): void {
    // formControlName automatically captures the value, just validate
    // Use setTimeout to ensure form value is updated after Angular's change detection
    setTimeout(() => {
      if (!event.value) {
        this.dateRangeError = '';
        this.historicReportForm.get('endDate')?.setValue(null);
        return;
      }
      
      // Reset end date if it's now invalid (more than 92 days from new start date)
      const endDateValue = this.historicReportForm.get('endDate')?.value;
      if (endDateValue) {
        const endDate = this.ensureDate(endDateValue);
        const maxEndDate = this.getMaxEndDate();
        if (maxEndDate && endDate && endDate > maxEndDate) {
          this.historicReportForm.get('endDate')?.setValue(null);
          this.dateRangeError = '';
        } else {
          // Validate - this will set or clear error appropriately
          this.validateDateRange();
        }
      } else {
        this.dateRangeError = '';
      }
    }, 0);
  }

  onEndDateChange(event: MatDatepickerInputEvent<Date>): void {
    // formControlName automatically captures the value, just validate
    // Use setTimeout to ensure form value is updated after Angular's change detection
    setTimeout(() => {
      if (!event.value) {
        this.dateRangeError = '';
        return;
      }
      
      const startDateValue = this.historicReportForm.get('startDate')?.value;
      if (startDateValue) {
        // Validate - this will set or clear error appropriately
        this.validateDateRange();
      } else {
        this.dateRangeError = '';
      }
    }, 0);
  }

  canDownloadReport(): boolean {
    // Don't allow download if already downloading
    if (this.isDownloadingReport) {
      return false;
    }
    
    const startDateValue = this.historicReportForm.get('startDate')?.value;
    const endDateValue = this.historicReportForm.get('endDate')?.value;
    
    // Check if both dates are selected
    if (!startDateValue || !endDateValue) {
      return false;
    }
    
    // Ensure dates are Date objects
    const startDate = this.ensureDate(startDateValue);
    const endDate = this.ensureDate(endDateValue);
    
    if (!startDate || !endDate) {
      return false;
    }
    
    // Check if there's a validation error
    if (this.dateRangeError && this.dateRangeError.trim().length > 0) {
      return false;
    }
    
    // Quick validation checks
    if (startDate > endDate) {
      return false;
    }
    
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 92) {
      return false;
    }
    
    // If we get here, everything is valid
    return true;
  }

  downloadHistoricReport(): void {
    if (!this.validateDateRange()) {
      return;
    }

    // Get dates from form
    const startDateValue = this.historicReportForm.get('startDate')?.value;
    const endDateValue = this.historicReportForm.get('endDate')?.value;
    
    // Ensure dates are Date objects
    const startDate = this.ensureDate(startDateValue);
    const endDate = this.ensureDate(endDateValue);

    if (!startDate || !endDate) {
      this.dateRangeError = 'Please select valid dates';
      return;
    }

    if (!this.orgId) {
      this.dateRangeError = 'Organization ID not found';
      return;
    }

    if (!this.warehouseCode) {
      this.dateRangeError = 'Warehouse code not found';
      return;
    }

    this.isDownloadingReport = true;
    this.downloadProgress = 'Fetching data...';

    // Convert dates to timestamps in milliseconds
    const fromDate = startDate.getTime();
    const toDate = endDate.getTime();

    // Fetch all data with pagination
    this.fetchAllHistoricReportData(this.orgId, this.warehouseCode, fromDate, toDate);
  }

  fetchAllHistoricReportData(gstin: string, warehouseCode: string, fromDate: number, toDate: number): void {
    const allData: any[] = [];
    let pageIndex = 1;
    const pageSize = 100;
    let hasMoreData = true;

    const fetchPage = (): void => {
      if (!hasMoreData) {
        // All data fetched, export to Excel
        this.exportToExcel(allData);
        return;
      }

      this.downloadProgress = `Fetching page ${pageIndex}...`;

      // Use the auth endpoint for authenticated requests
      const url = `${environment.conciergeUrl}/concierge/api/v1/mfg/${gstin}/historic-epod-report/auth/_search`;
      
      // Build request body
      const requestBody: any = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        sortOrder: 'DESC',
        sortPath: 'createdOn',
        filters: {
          fromDate: [fromDate],
          toDate: [toDate],
          warehouseCode: [warehouseCode]
        }
      };

      // Set up headers with auth token (no Bearer prefix for this API)
      let headers = new HttpHeaders({
        'Content-Type': 'application/json'
      });

      if (this.authToken) {
        headers = headers.set('Authorization', this.authToken);
      }

      this.http.post(url, requestBody, { headers }).subscribe({
        next: (response: any) => {
          // Handle response structure: { totalResults: number, rows: [...] }
          const currentPageData = response?.rows || response?.data || [];
          
          if (currentPageData && Array.isArray(currentPageData) && currentPageData.length > 0) {
            allData.push(...currentPageData);
            
            // Check if there's more data
            const totalRecords = response.totalResults || response.totalRecords || response.total || response.totalElements || 0;
            const fetchedRecords = allData.length;
            
            this.downloadProgress = `Fetched ${fetchedRecords} of ${totalRecords} records...`;
            
            // If current page has less data than pageSize, we've reached the end
            if (currentPageData.length < pageSize) {
              hasMoreData = false;
            } else if (totalRecords > 0 && fetchedRecords >= totalRecords) {
              hasMoreData = false;
            } else {
              // Continue to next page
              pageIndex++;
            }
            
            // Continue fetching next page or export
            fetchPage();
          } else {
            // No more data or unexpected response
            hasMoreData = false;
            if (allData.length === 0) {
              this.isDownloadingReport = false;
              this.downloadProgress = '';
              this.dateRangeError = 'No data found for the selected date range';
            } else {
              this.exportToExcel(allData);
            }
          }
        },
        error: (error) => {
          console.error('Error fetching historic report:', error);
          this.isDownloadingReport = false;
          this.downloadProgress = '';
          this.dateRangeError = 'Error fetching historic report. Please try again.';
        }
      });
    };

    // Start fetching
    fetchPage();
  }

  exportToExcel(data: any[]): void {
    if (!data || data.length === 0) {
      this.isDownloadingReport = false;
      this.downloadProgress = '';
      this.dateRangeError = 'No data to export';
      return;
    }

    this.downloadProgress = 'Generating Excel file...';

    try {
      // Helper function to format timestamp to readable date
      const formatDate = (timestamp: number | string | null | undefined): string => {
        if (!timestamp) return '';
        
        let ts: number;
        if (typeof timestamp === 'string') {
          ts = parseInt(timestamp, 10);
        } else {
          ts = timestamp;
        }
        
        if (isNaN(ts)) return '';
        
        // Check if it's a timestamp in milliseconds
        if (ts > 1000000000000) {
          const date = new Date(ts);
          // Format as DD/MM/YYYY HH:MM:SS
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        }
        return String(timestamp);
      };

      // Map data to formatted Excel format with exact column order as specified
      const formattedData = data.map(item => {
        return {
          'LR Date': formatDate(item.lrDate),
          'Transporter name': item.transporterName || '-',
          'Reported Date': formatDate(item.reportedDate),
          'Unloaded Date': formatDate(item.unloadedDate),
          'Truck number': item.truckNumber || '-',
          'LR  Number': item.lrNumber || '-',
          'Origin Warehouse': item.originWarehouse || '-',
          'Destination Warehouse': item.destinationWarehouse || '-',
          'Invoice number': item.invoiceNumber || item.invoiceNo || item.invoice || '-',
          'Product name': item.productName || '-',
          'Qty Received': item.qtyReceived || 0,
          'Shortages': item.shortages || 0,
          'Damages': item.breakageQty || item.damages || 0,
          'POD Remarks': item.podRemarks || '-',
          'Received Date': formatDate(item.receivedDate),
          'Receiver Contact number': item.receiverContactNumber || '-',
          'Receiver Name': item.receiverName || item.receiver || '-',
          'Org Name': item.orgName || '-'
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Historic Report');

      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // LR Date
        { wch: 30 }, // Transporter name
        { wch: 20 }, // Reported Date
        { wch: 20 }, // Unloaded Date
        { wch: 15 }, // Truck number
        { wch: 15 }, // LR  Number
        { wch: 25 }, // Origin Warehouse
        { wch: 25 }, // Destination Warehouse
        { wch: 18 }, // Invoice number
        { wch: 40 }, // Product name
        { wch: 12 }, // Qty Received
        { wch: 12 }, // Shortages
        { wch: 12 }, // Damages
        { wch: 50 }, // POD Remarks
        { wch: 20 }, // Received Date
        { wch: 20 }, // Receiver Contact number
        { wch: 25 }, // Receiver Name
        { wch: 30 }  // Org Name
      ];
      worksheet['!cols'] = columnWidths;

      // Generate filename with date range
      const startDateValue = this.historicReportForm.get('startDate')?.value;
      const endDateValue = this.historicReportForm.get('endDate')?.value;
      const startDate = this.ensureDate(startDateValue);
      const endDate = this.ensureDate(endDateValue);
      const startDateStr = startDate?.toISOString().split('T')[0] || 'start';
      const endDateStr = endDate?.toISOString().split('T')[0] || 'end';
      const filename = `Historic_EPOD_Report_${startDateStr}_to_${endDateStr}.xlsx`;

      // Write file
      XLSX.writeFile(workbook, filename);

      this.isDownloadingReport = false;
      this.downloadProgress = '';
      
      // Format dates for display
      const formatDisplayDate = (date: Date | null): string => {
        if (!date) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      };
      
      // Show success message with date range
      this.downloadedRecordsCount = data.length;
      this.downloadedDateRange = `${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`;
      this.showDownloadSuccess = true;
      this.showHistoricReportModal = false;
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.isDownloadingReport = false;
      this.downloadProgress = '';
      this.dateRangeError = 'Error exporting to Excel. Please try again.';
    }
  }

  closeSuccessModal(): void {
    this.showDownloadSuccess = false;
    this.downloadedRecordsCount = 0;
    this.downloadedDateRange = '';
  }

  downloadAnotherReport(): void {
    this.showDownloadSuccess = false;
    this.downloadedRecordsCount = 0;
    this.downloadedDateRange = '';
    this.openHistoricReportModal();
  }
}
